import { useState, useMemo, useCallback } from 'react';
import api from '../../hooks/useApi';
import { useToast } from '../ui/Toast';
import { toCsv } from '../../utils/csv.js';

const PAGE_SIZE = 20;
const IMPORT_CHUNK = 8;
const MAX_UPLOAD_MB = 70; // matches shared FileUpload.jsx — base64-in-JSON transport ceiling

// Filenames look like "AV-401658 - Electrical Block Ground Floor Lighting.pdf",
// "AV- 401739 - West Wing...", "AV -401757 - ..." — inconsistent spacing around
// the hyphen, so match loosely and reconstruct a clean "AV-XXXXXX" reference.
function parseReferenceFromFilename(name) {
  const m = /AV[\s-]*(\d{5,7})/i.exec(name);
  return m ? `AV-${m[1]}` : null;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function downloadCsv(filename, header, rows) {
  const csv = toCsv(header, rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

export default function SowDocumentBackfillPage() {
  const { toast } = useToast();

  // ── Local-folder upload (primary flow) ──────────────────────────────────
  const [localFiles, setLocalFiles] = useState([]);   // [{ file, name, reference, status, sowId, error }]
  const [matching, setMatching]     = useState(false);
  const [uploading, setUploading]   = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });

  const onLocalFilesSelected = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    setMatching(true);

    const parsed = fileList.map(file => ({
      file, name: file.name, reference: parseReferenceFromFilename(file.name),
      status: 'checking', sowId: null, error: null,
    }));

    try {
      const references = [...new Set(parsed.map(p => p.reference).filter(Boolean))];
      const { found } = references.length ? await api.post('sow-document-backfill/lookup', { references }) : { found: {} };

      // Detect two local files targeting the same SOW — can't both be "the" document.
      const refCounts = {};
      for (const p of parsed) if (p.reference) refCounts[p.reference] = (refCounts[p.reference] || 0) + 1;

      for (const p of parsed) {
        if (!p.reference) { p.status = 'unparsed'; continue; }
        if (refCounts[p.reference] > 1) { p.status = 'duplicate'; continue; }
        const match = found[p.reference];
        if (!match) { p.status = 'not_found'; continue; }
        if (match.has_document) { p.status = 'has_document'; continue; }
        p.status = 'ready'; p.sowId = match.sow_id;
      }
      setLocalFiles(parsed);
    } catch (err) {
      toast.error(err.message || 'Could not match files against SOWs.');
    }
    setMatching(false);
  };

  const uploadLocalFiles = async () => {
    const ready = localFiles.filter(f => f.status === 'ready');
    if (!ready.length) return;
    setUploading(true);
    setUploadProgress({ done: 0, total: ready.length });
    let ok = 0, fail = 0;
    for (let i = 0; i < ready.length; i++) {
      const f = ready[i];
      try {
        if (f.file.size > MAX_UPLOAD_MB * 1024 * 1024) throw new Error(`Too large (max ${MAX_UPLOAD_MB}MB)`);
        const base64 = await fileToBase64(f.file);
        const contentType = f.file.type || (f.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'application/octet-stream');
        const uploaded = await api.post('uploads', { filename: f.name, content_type: contentType, data: base64 });
        await api.post(`sows/${f.sowId}/documents`, {
          document_type: 'sow', title: `Statement of Work - ${f.reference}`, url: uploaded.url,
          notes: `Backfilled from local folder upload (${f.name}) during document migration.`,
        });
        setLocalFiles(prev => prev.map(x => x === f ? { ...x, status: 'done' } : x));
        ok++;
      } catch (err) {
        setLocalFiles(prev => prev.map(x => x === f ? { ...x, status: 'error', error: err.message || 'Upload failed' } : x));
        fail++;
      }
      setUploadProgress({ done: i + 1, total: ready.length });
    }
    setUploading(false);
    toast[fail ? 'error' : 'success'](`Uploaded ${ok} document${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}.`);
  };

  const localBuckets = useMemo(() => {
    const b = { ready: [], done: [], error: [], hasDocument: [], notFound: [], duplicate: [], unparsed: [] };
    for (const f of localFiles) {
      if (f.status === 'ready') b.ready.push(f);
      else if (f.status === 'done') b.done.push(f);
      else if (f.status === 'error') b.error.push(f);
      else if (f.status === 'has_document') b.hasDocument.push(f);
      else if (f.status === 'not_found') b.notFound.push(f);
      else if (f.status === 'duplicate') b.duplicate.push(f);
      else if (f.status === 'unparsed') b.unparsed.push(f);
    }
    return b;
  }, [localFiles]);

  // ── SharePoint scan (secondary/fallback flow) ───────────────────────────
  const [scanning, setScanning]   = useState(false);
  const [scanned, setScanned]     = useState(false);
  const [progress, setProgress]   = useState({ done: 0, total: 0 });
  const [results, setResults]     = useState([]);       // raw candidate rows from the scan
  const [selectedReady, setSelectedReady] = useState(new Set());
  const [reviewChoice, setReviewChoice]   = useState({}); // sow_id -> drive_item_id
  const [importing, setImporting] = useState(false);
  const [importOutcome, setImportOutcome] = useState({}); // sow_id -> { success, error }

  const buckets = useMemo(() => {
    const ready = [], needsReview = [], noFolder = [], noFiles = [];
    for (const r of results) {
      if (importOutcome[r.sow_id]?.success) continue; // drop rows already imported
      if (r.outcome === 'ready') ready.push(r);
      else if (r.outcome === 'needs_review') needsReview.push(r);
      else if (r.outcome === 'no_folder') noFolder.push(r);
      else if (r.outcome === 'no_files') noFiles.push(r);
    }
    return { ready, needsReview, noFolder, noFiles };
  }, [results, importOutcome]);

  const runScan = useCallback(async () => {
    setScanning(true); setScanned(false);
    setResults([]); setSelectedReady(new Set()); setReviewChoice({}); setImportOutcome({});
    let offset = 0, total = Infinity;
    const all = [];
    try {
      while (offset < total) {
        const page = await api.get('sow-document-backfill/candidates', { offset, limit: PAGE_SIZE });
        total = page.total || 0;
        all.push(...(page.results || []));
        offset += PAGE_SIZE;
        setProgress({ done: Math.min(offset, total), total });
        setResults([...all]);
      }
      setSelectedReady(new Set(all.filter(r => r.outcome === 'ready').map(r => r.sow_id)));
      setScanned(true);
    } catch (e) {
      toast.error(e.message || 'Scan failed');
    }
    setScanning(false);
  }, [toast]);

  const runImport = useCallback(async (items) => {
    if (!items.length) return;
    setImporting(true);
    let ok = 0, fail = 0;
    for (let i = 0; i < items.length; i += IMPORT_CHUNK) {
      const chunk = items.slice(i, i + IMPORT_CHUNK);
      try {
        const { results: chunkResults } = await api.post('sow-document-backfill/import', { items: chunk });
        setImportOutcome(prev => {
          const next = { ...prev };
          for (const r of chunkResults || []) next[r.sow_id] = r;
          return next;
        });
        ok   += (chunkResults || []).filter(r => r.success).length;
        fail += (chunkResults || []).filter(r => !r.success).length;
      } catch (e) {
        for (const it of chunk) setImportOutcome(prev => ({ ...prev, [it.sow_id]: { success: false, error: e.message || 'Import failed' } }));
        fail += chunk.length;
      }
    }
    setImporting(false);
    toast[fail ? 'error' : 'success'](`Imported ${ok} document${ok === 1 ? '' : 's'}${fail ? `, ${fail} failed` : ''}.`);
  }, [toast]);

  const importReady = () => {
    const items = buckets.ready
      .filter(r => selectedReady.has(r.sow_id))
      .map(r => ({ sow_id: r.sow_id, drive_item_id: r.candidates[0].id, file_name: r.candidates[0].name }));
    runImport(items);
  };

  const importResolved = () => {
    const items = buckets.needsReview
      .filter(r => reviewChoice[r.sow_id])
      .map(r => {
        const cand = r.candidates.find(c => c.id === reviewChoice[r.sow_id]);
        return { sow_id: r.sow_id, drive_item_id: cand.id, file_name: cand.name };
      });
    runImport(items);
  };

  const exportReport = () => {
    const header = ['SOW', 'Client', 'Status', 'Outcome', 'Candidate files', 'Import result'];
    const rows = results.map(r => [
      r.reference, r.client_name || '', r.status,
      r.outcome,
      (r.candidates || []).map(c => c.name).join(' | '),
      importOutcome[r.sow_id]?.success ? 'Imported' : importOutcome[r.sow_id]?.error || '',
    ]);
    downloadCsv('sow-document-backfill-report.csv', header, rows);
  };

  const toggleReady = (sowId) => {
    setSelectedReady(prev => {
      const next = new Set(prev);
      next.has(sowId) ? next.delete(sowId) : next.add(sowId);
      return next;
    });
  };

  return (
    <div className="fade-in p-4">
      <div className="mb-4">
        <h1 className="text-base font-extrabold text-navy">SOW Document Backfill</h1>
        <div className="text-[9px] text-gray-500 mt-0.5">
          One-off migration tool. Select every issued SOW PDF from a local folder — the SOW reference is matched from
          each filename, and each file is uploaded one by one to its matching SOW. Anything already having a document,
          not matching a known SOW, or ambiguous (two files claiming the same SOW) is left for you to resolve — nothing
          is guessed or overwritten.
        </div>
      </div>

      {/* ── Local folder upload ──────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded p-4 mb-4">
        <div className="text-[10px] font-bold text-navy mb-2">Upload from a local folder</div>
        <input type="file" multiple accept=".pdf,.doc,.docx" onChange={onLocalFilesSelected} disabled={matching || uploading} className="text-[10px]" />
        {matching && <div className="text-[9px] text-gray-400 mt-2">Matching filenames against SOWs…</div>}

        {localFiles.length > 0 && !matching && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 my-3">
              {[
                { label: 'Ready', value: localBuckets.ready.length, color: 'text-emerald-600' },
                { label: 'Uploaded', value: localBuckets.done.length, color: 'text-emerald-700' },
                { label: 'Already has doc', value: localBuckets.hasDocument.length, color: 'text-gray-500' },
                { label: 'No matching SOW', value: localBuckets.notFound.length, color: 'text-gray-500' },
                { label: 'Duplicate target', value: localBuckets.duplicate.length, color: 'text-amber-600' },
                { label: 'Failed', value: localBuckets.error.length, color: 'text-red-600' },
              ].map(s => (
                <div key={s.label} className="bg-gray-50 border border-gray-100 rounded-lg px-2 py-1.5 text-center">
                  <div className={`text-[14px] font-black ${s.color}`}>{s.value}</div>
                  <div className="text-[7px] font-bold text-gray-400 uppercase tracking-wide">{s.label}</div>
                </div>
              ))}
            </div>

            <button onClick={uploadLocalFiles} disabled={uploading || !localBuckets.ready.length}
              className="px-3 py-1.5 text-[10px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 mb-3">
              {uploading ? `Uploading… (${uploadProgress.done}/${uploadProgress.total})` : `Upload ${localBuckets.ready.length} file${localBuckets.ready.length === 1 ? '' : 's'}`}
            </button>

            {(localBuckets.duplicate.length > 0 || localBuckets.notFound.length > 0 || localBuckets.unparsed.length > 0 || localBuckets.error.length > 0) && (
              <div className="border border-gray-200 rounded overflow-x-auto max-h-64 overflow-y-auto">
                <table className="w-full">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">File</th>
                      <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Reference</th>
                      <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">Issue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...localBuckets.duplicate, ...localBuckets.notFound, ...localBuckets.unparsed, ...localBuckets.error].map((f, i) => (
                      <tr key={i} className="border-t border-gray-100">
                        <td className="px-2 py-1 text-[9px] text-gray-700 max-w-[300px] truncate">{f.name}</td>
                        <td className="px-2 py-1 text-[9px] text-gray-500">{f.reference || '—'}</td>
                        <td className="px-2 py-1 text-[9px] text-amber-600">
                          {f.status === 'duplicate' ? 'Another selected file also matches this SOW reference'
                            : f.status === 'not_found' ? 'No SOW found with this reference'
                            : f.status === 'unparsed' ? "Couldn't find an AV-###### reference in the filename"
                            : f.error}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── SharePoint scan (secondary/fallback flow) ────────────────────── */}
      <div className="text-[9px] font-bold text-gray-400 uppercase mb-2">Or — scan SharePoint instead</div>
      <div className="flex items-center gap-2 mb-4">
        <button onClick={runScan} disabled={scanning || importing}
          className="px-3 py-1.5 text-[10px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-40">
          {scanning ? `Scanning… (${progress.done}/${progress.total})` : scanned ? 'Re-scan' : 'Scan for missing SOW documents'}
        </button>
        {scanned && (
          <button onClick={exportReport} className="px-3 py-1.5 text-[10px] font-bold border border-gray-200 rounded bg-white hover:bg-gray-50">
            ⬇ Export report
          </button>
        )}
      </div>

      {scanned && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {[
            { label: 'Ready to import', value: buckets.ready.length, color: 'text-emerald-600' },
            { label: 'Needs your review', value: buckets.needsReview.length, color: 'text-amber-600' },
            { label: 'No SharePoint folder', value: buckets.noFolder.length, color: 'text-gray-500' },
            { label: 'Folder has no files', value: buckets.noFiles.length, color: 'text-gray-500' },
          ].map(s => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-lg px-3 py-2 text-center">
              <div className={`text-[16px] font-black ${s.color}`}>{s.value}</div>
              <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wide mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {scanned && buckets.ready.length > 0 && (
        <div className="bg-white border border-gray-200 rounded mb-4">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-emerald-50/60">
            <div className="text-[9px] font-bold text-emerald-700 uppercase">Ready to import — exactly one file found</div>
            <button onClick={importReady} disabled={importing || !selectedReady.size}
              className="px-2 py-1 text-[9px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40">
              Import {selectedReady.size} selected
            </button>
          </div>
          <table className="w-full">
            <tbody>
              {buckets.ready.map(r => (
                <tr key={r.sow_id} className="border-b border-gray-100">
                  <td className="px-3 py-1.5 w-6">
                    <input type="checkbox" checked={selectedReady.has(r.sow_id)} onChange={() => toggleReady(r.sow_id)} />
                  </td>
                  <td className="px-2 py-1.5 text-[10px] font-semibold text-navy whitespace-nowrap">{r.reference}</td>
                  <td className="px-2 py-1.5 text-[10px] text-gray-600">{r.client_name || '—'}</td>
                  <td className="px-2 py-1.5 text-[10px] text-gray-500">{r.candidates[0].name}</td>
                  <td className="px-2 py-1.5 text-[9px] text-right whitespace-nowrap">
                    {importOutcome[r.sow_id] ? (
                      importOutcome[r.sow_id].success
                        ? <span className="text-emerald-600 font-semibold">✓ Imported</span>
                        : <span className="text-red-500">{importOutcome[r.sow_id].error}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {scanned && buckets.needsReview.length > 0 && (
        <div className="bg-white border border-gray-200 rounded mb-4">
          <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 bg-amber-50/60">
            <div className="text-[9px] font-bold text-amber-700 uppercase">Needs review — more than one file in the folder</div>
            <button onClick={importResolved} disabled={importing || !Object.keys(reviewChoice).some(id => buckets.needsReview.some(r => r.sow_id === id))}
              className="px-2 py-1 text-[9px] font-bold bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-40">
              Import resolved
            </button>
          </div>
          <table className="w-full">
            <tbody>
              {buckets.needsReview.map(r => (
                <tr key={r.sow_id} className="border-b border-gray-100">
                  <td className="px-3 py-1.5 text-[10px] font-semibold text-navy whitespace-nowrap">{r.reference}</td>
                  <td className="px-2 py-1.5 text-[10px] text-gray-600">{r.client_name || '—'}</td>
                  <td className="px-2 py-1.5">
                    <select className="form-input text-[9px] py-0.5" value={reviewChoice[r.sow_id] || ''}
                      onChange={e => setReviewChoice(prev => ({ ...prev, [r.sow_id]: e.target.value }))}>
                      <option value="">— choose file —</option>
                      {r.candidates.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1.5 text-[9px] text-right whitespace-nowrap">
                    {importOutcome[r.sow_id] ? (
                      importOutcome[r.sow_id].success
                        ? <span className="text-emerald-600 font-semibold">✓ Imported</span>
                        : <span className="text-red-500">{importOutcome[r.sow_id].error}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {scanned && (buckets.noFolder.length > 0 || buckets.noFiles.length > 0) && (
        <div className="bg-white border border-gray-200 rounded mb-4">
          <div className="px-3 py-2 border-b border-gray-200 bg-gray-50">
            <div className="text-[9px] font-bold text-gray-500 uppercase">No file found — resolve manually</div>
          </div>
          <table className="w-full">
            <tbody>
              {[...buckets.noFolder, ...buckets.noFiles].map(r => (
                <tr key={r.sow_id} className="border-b border-gray-100">
                  <td className="px-3 py-1.5 text-[10px] font-semibold text-navy whitespace-nowrap">{r.reference}</td>
                  <td className="px-2 py-1.5 text-[10px] text-gray-600">{r.client_name || '—'}</td>
                  <td className="px-2 py-1.5 text-[10px] text-gray-500">{r.status}</td>
                  <td className="px-2 py-1.5 text-[9px] text-gray-400">
                    {r.outcome === 'no_folder' ? 'No SharePoint folder found for this reference' : 'Folder exists but has no PDF/Word file'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {scanned && !buckets.ready.length && !buckets.needsReview.length && !buckets.noFolder.length && !buckets.noFiles.length && (
        <div className="text-center py-12 text-[10px] text-gray-400">Every SOW already has a document — nothing to backfill.</div>
      )}
    </div>
  );
}
